/*
 * Yugal storefront behaviours: scroll reveals, the photo → portrait compare slider and
 * the per-style music toggle. Progressive enhancement: every section reads fine without JS.
 */
(() => {
  document.documentElement.classList.add('y-js');

  // ------------------------------------------------------------ reveals
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const revealAll = (root = document) =>
    root.querySelectorAll('[data-y-reveal]').forEach((el) => el.classList.add('is-revealed'));

  if (reduced || !('IntersectionObserver' in window)) {
    revealAll();
  } else {
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }
    );
    const observe = (root = document) =>
      root.querySelectorAll('[data-y-reveal]:not(.is-revealed)').forEach((el) => io.observe(el));
    // Opening sections animate in on load: content above the fold should never wait for a scroll.
    requestAnimationFrame(() => revealAll(document.querySelector('.y-hero, .y-style-hero') ?? document.createElement('div')));
    observe();
    // Theme editor re-renders sections in place.
    document.addEventListener('shopify:section:load', (event) => observe(event.target));
  }

  // ------------------------------------------------------------ compare slider
  class YugalCompare extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('input[type="range"]');
      if (!this.range) return;
      this.update = () => this.style.setProperty('--y-split', `${this.range.value}%`);
      this.range.addEventListener('input', this.update);
      this.update();
    }

    disconnectedCallback() {
      this.range?.removeEventListener('input', this.update);
    }
  }
  if (!customElements.get('yugal-compare')) customElements.define('yugal-compare', YugalCompare);

  // ------------------------------------------------------------ style music
  // Off by default; the visitor opts in. One track plays at a time; preference is remembered.
  const MUSIC_KEY = 'yugal:music';
  /**
   * <yugal-music>: the tradition's music on a style page. It starts by itself: right away if the
   * browser allows sound, otherwise on the visitor's first tap, click or key press anywhere on the
   * page (browsers require that). A floating control in the corner shows it is playing and pauses
   * it in one tap. A pause is remembered (yugal:music = 'off'), so it won't start again by itself.
   */
  class YugalMusic extends HTMLElement {
    connectedCallback() {
      this.audio = this.querySelector('audio');
      this.button = this.querySelector('button');
      if (!this.audio || !this.button) return;
      this.volume = 0.5;
      this.onClick = () => (this.audio.paused ? this.play() : this.pause());
      this.button.addEventListener('click', this.onClick);
      this.audio.addEventListener('ended', () => this.render(false));
      this.audio.addEventListener('pause', () => this.render(false));
      this.audio.addEventListener('play', () => this.render(true));
      this.buildFloat();
      this.render(false);

      // Pause while the tab is hidden; carry on when the visitor comes back.
      this.onVisibility = () => {
        if (document.hidden && !this.audio.paused) {
          this.resumeOnReturn = true;
          this.audio.pause();
        } else if (!document.hidden && this.resumeOnReturn) {
          this.resumeOnReturn = false;
          this.play({ remember: false });
        }
      };
      document.addEventListener('visibilitychange', this.onVisibility);

      let pref = null;
      try { pref = localStorage.getItem(MUSIC_KEY); } catch {}
      if (pref !== 'off') this.autoplay();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.disarm?.();
      this.float?.remove();
      this.audio?.pause();
    }

    /** Try now; if the browser blocks sound, start on the first interaction instead. */
    async autoplay() {
      if (await this.play({ remember: false, quiet: true })) return;
      const start = (event) => {
        // The music controls handle their own clicks.
        if (event.target?.closest?.('yugal-music, .y-music-float')) return;
        this.disarm();
        this.play({ remember: false });
      };
      const events = ['pointerdown', 'keydown', 'touchend'];
      events.forEach((type) => document.addEventListener(type, start, { capture: true, passive: true }));
      this.disarm = () => {
        events.forEach((type) => document.removeEventListener(type, start, { capture: true }));
        this.disarm = null;
      };
    }

    async play({ remember = true, quiet = false } = {}) {
      document.querySelectorAll('yugal-music').forEach((el) => el !== this && el.pause?.({ remember: false }));
      this.disarm?.();
      try {
        this.audio.volume = 0;
        await this.audio.play();
        this.fadeTo(this.volume);
        if (remember) try { localStorage.setItem(MUSIC_KEY, 'on'); } catch {}
        return true;
      } catch {
        this.audio.volume = this.volume;
        if (!quiet) this.render(false);
        return false;
      }
    }

    /** remember: false for pauses the visitor didn't ask for (recording a voice note, another player). */
    pause({ remember = true } = {}) {
      this.disarm?.();
      this.audio?.pause();
      if (remember) try { localStorage.setItem(MUSIC_KEY, 'off'); } catch {}
    }

    fadeTo(target, ms = 1800) {
      cancelAnimationFrame(this.fade);
      const from = this.audio.volume;
      const began = performance.now();
      const step = (now) => {
        const k = Math.min(1, (now - began) / ms);
        try { this.audio.volume = from + (target - from) * k; } catch {}
        if (k < 1) this.fade = requestAnimationFrame(step);
      };
      this.fade = requestAnimationFrame(step);
    }

    buildFloat() {
      const float = document.createElement('button');
      float.type = 'button';
      float.className = 'y-music-float';
      float.innerHTML = '<span class="y-music__bars" aria-hidden="true"><i></i><i></i><i></i></span><span data-float-label></span>';
      float.addEventListener('click', this.onClick);
      document.body.append(float);
      this.float = float;
    }

    render(playing) {
      this.toggleAttribute('playing', playing);
      this.button.setAttribute('aria-pressed', String(playing));
      const label = this.button.querySelector('[data-label]');
      if (label) label.textContent = playing ? this.dataset.labelOn : this.dataset.labelOff;
      if (this.float) {
        this.float.toggleAttribute('data-playing', playing);
        this.float.setAttribute('aria-pressed', String(playing));
        this.float.setAttribute('aria-label', playing ? 'Pause the music' : 'Play the music');
        this.float.querySelector('[data-float-label]').textContent = playing ? 'Music on · Pause' : 'Play music';
      }
    }
  }
  if (!customElements.get('yugal-music')) customElements.define('yugal-music', YugalMusic);
})();
